import * as z from "zod";
import type { ApiContext } from "../context";
import {
  type ApiNotAuthorized,
  CardNotFound,
  CartItemNotFound,
  CheckoutRefused,
  CustomerCartNotFound,
  ErrorCode,
  ScanNotFound,
  ScanNotReviewable,
  UnexpectedApiError,
  ValidationError,
} from "../errors";
import { StoreCustomer } from "../model/card";
import { parse } from "../model/common";
import { AdminCustomerCart, CustomerCart, type CustomerCartItemEdit } from "../model/customer-cart";
import { Scan, ScanConfirmation, type ScanConfirmOptions, ScanReviewGroup } from "../model/scan";
import { forward, type Result } from "../result";
import type { Transport } from "../transport";

const CartList = z.object({ requests: z.array(AdminCustomerCart) });
const PendingCount = z.object({ count: z.number() });
const ReviewQueue = z.object({ groups: z.array(ScanReviewGroup) });
const ScanEnvelope = z.object({ scan: Scan });
const Customers = z.object({ customers: z.array(StoreCustomer) });
const Repriced = z.object({ priceCents: z.number().nullable() });

/** What the cashier is looking at. The list defaults to OPEN. */
export type PosCartStatus = "OPEN" | "PAID" | "EXPIRED";

/** Narrows the scan list. `customerId` is what the customer drill-down passes. */
export interface PosScanQuery {
  enableNoMatch?: boolean;
  customerId?: string;
  locationId?: string;
  review?: "PENDING" | "CONFIRMED" | "REJECTED";
}

/**
 * `/pos`: what a Shopify POS extension may do.
 *
 * A deliberately narrow slice of the merchant surface, and narrow in a way that
 * matters — there is no store id anywhere in it. Every route is behind
 * `@PosAuth()`, which reads the shop out of the App Bridge session token, so a
 * till can only ever act on the shop it is signed in to. That is why these are
 * separate routes rather than the `/shop/:shopId/...` ones with a different
 * guard: a path parameter would be a store id a cashier could change.
 *
 * The operations underneath are the same ones the browser calls — see the
 * feature map in `hobby-pro/CLAUDE.md`. Exposing something new to POS is a thin
 * controller method over an existing op, not new business logic, and it belongs
 * here when it lands.
 *
 * TODO: nothing in this group has e2e coverage. `Sugar` mints Firebase tokens,
 * and `@PosAuth()` takes a Shopify session token — a different signer — so
 * covering it needs the suite to mint one of those, which is the real work.
 * Until then every one of these is exercised only by hand, on a till.
 */
export class PosApi {
  constructor(private readonly t: Transport) {}

  // ─── Customer carts ───────────────────────────────────────────────────────

  /** The till's queue: carts a customer has built for the cashier to review. */
  async customerCarts(
    ctx: ApiContext,
    opts: { status?: PosCartStatus } = {},
  ): Promise<Result<AdminCustomerCart[], ApiNotAuthorized>> {
    const r = await this.t.get(ctx, "/pos/customer-carts", { query: { status: opts.status } });
    return forward(
      r,
      (j) => parse(CartList, j).requests,
      (e) => {
        if (e instanceof ValidationError) throw new UnexpectedApiError(e);
        return e;
      },
    );
  }

  /** How many are waiting. What the home tile's badge counts. */
  async pendingCount(ctx: ApiContext): Promise<Result<number, ApiNotAuthorized>> {
    const r = await this.t.get(ctx, "/pos/customer-carts/count");
    return forward(
      r,
      (j) => parse(PendingCount, j).count,
      (e) => {
        if (e instanceof ValidationError) throw new UnexpectedApiError(e);
        return e;
      },
    );
  }

  /**
   * Edits one line — what the cashier changes when the customer asks to leave a
   * card out, or agrees a different price at the counter.
   */
  async editCartItem(
    ctx: ApiContext,
    customerCartId: string,
    itemId: string,
    edit: CustomerCartItemEdit,
  ): Promise<Result<CustomerCart, ApiNotAuthorized | CustomerCartNotFound | CartItemNotFound | CheckoutRefused>> {
    const r = await this.t.patch(ctx, `/pos/customer-carts/${customerCartId}/items/${itemId}`, { body: edit });
    return forward(
      r,
      (j) => parse(CustomerCart, j),
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue === "itemId") return new CartItemNotFound(itemId, e);
          if (e.incorrectValue === "status") return new CheckoutRefused(e.detailedDesc, e);
          return new CustomerCartNotFound(customerCartId, e);
        }
        return e;
      },
    );
  }

  /** Throws the cart away. Refused once it is PAID — that one is history, not a draft. */
  async removeCustomerCart(
    ctx: ApiContext,
    customerCartId: string,
  ): Promise<Result<void, ApiNotAuthorized | CustomerCartNotFound | CheckoutRefused>> {
    const r = await this.t.delete(ctx, `/pos/customer-carts/${customerCartId}`);
    return forward(
      r,
      () => undefined,
      (e) => {
        if (e instanceof ValidationError) {
          if (e.code === ErrorCode.IncorrectState) return new CheckoutRefused(e.detailedDesc, e);
          return new CustomerCartNotFound(customerCartId, e);
        }
        return e;
      },
    );
  }

  // ─── Scans ────────────────────────────────────────────────────────────────

  /** Every scan in the shop, newest first. `customerId` is the drill-down. */
  async scans(ctx: ApiContext, query: PosScanQuery = {}): Promise<Result<Scan[], ApiNotAuthorized>> {
    const r = await this.t.get(ctx, "/pos/scan/all", { query: { ...query } });
    return forward(
      r,
      (j) => parse(z.array(Scan), j),
      (e) => {
        if (e instanceof ValidationError) throw new UnexpectedApiError(e);
        return e;
      },
    );
  }

  /** The review queue, grouped the same way the merchant app groups it. */
  async reviewQueue(
    ctx: ApiContext,
    opts: { enableNoMatch?: boolean } = {},
  ): Promise<Result<ScanReviewGroup[], ApiNotAuthorized>> {
    const r = await this.t.get(ctx, "/pos/scan/review", { query: opts });
    return forward(
      r,
      (j) => parse(ReviewQueue, j).groups,
      (e) => {
        if (e instanceof ValidationError) throw new UnexpectedApiError(e);
        return e;
      },
    );
  }

  async scan(
    ctx: ApiContext,
    scanId: string,
    opts: { enableNoMatch?: boolean } = {},
  ): Promise<Result<Scan, ApiNotAuthorized | ScanNotFound>> {
    const r = await this.t.get(ctx, `/pos/scan/${scanId}`, { query: opts });
    return forward(
      r,
      (j) => parse(ScanEnvelope, j).scan,
      (e) => {
        if (e instanceof ValidationError) return new ScanNotFound(scanId, e);
        return e;
      },
    );
  }

  /** Approves the scan, creating the card and — with `stock` — the lot behind it. */
  async confirmScan(
    ctx: ApiContext,
    scanId: string,
    options: ScanConfirmOptions = {},
  ): Promise<Result<ScanConfirmation, ApiNotAuthorized | ScanNotFound | ScanNotReviewable>> {
    const r = await this.t.post(ctx, `/pos/scan/${scanId}/confirm`, { body: options });
    return forward(
      r,
      (j) => parse(ScanConfirmation, j),
      (e) => {
        if (e instanceof ValidationError) {
          if (e.code === ErrorCode.DoesntExist) return new ScanNotFound(scanId, e);
          return new ScanNotReviewable(e.detailedDesc, e);
        }
        return e;
      },
    );
  }

  /** Turns the scan down. The card is not created and nothing is stocked. */
  async rejectScan(
    ctx: ApiContext,
    scanId: string,
  ): Promise<Result<void, ApiNotAuthorized | ScanNotFound | ScanNotReviewable>> {
    const r = await this.t.post(ctx, `/pos/scan/${scanId}/reject`);
    return forward(
      r,
      () => undefined,
      (e) => {
        if (e instanceof ValidationError) {
          if (e.code === ErrorCode.DoesntExist) return new ScanNotFound(scanId, e);
          return new ScanNotReviewable(e.detailedDesc, e);
        }
        return e;
      },
    );
  }

  /** A fresh upstream price for the card. Null means the source had nothing. */
  async repriceCard(ctx: ApiContext, cardId: string): Promise<Result<number | null, ApiNotAuthorized | CardNotFound>> {
    const r = await this.t.post(ctx, `/pos/card/${cardId}/reprice`);
    return forward(
      r,
      (j) => parse(Repriced, j).priceCents,
      (e) => {
        if (e instanceof ValidationError) return new CardNotFound(cardId, e);
        return e;
      },
    );
  }

  /** The shop's customers who currently hold valid scans. */
  async customers(
    ctx: ApiContext,
    opts: { enableNoMatch?: boolean } = {},
  ): Promise<Result<StoreCustomer[], ApiNotAuthorized>> {
    const r = await this.t.get(ctx, "/pos/customers", { query: opts });
    return forward(
      r,
      (j) => parse(Customers, j).customers,
      (e) => {
        if (e instanceof ValidationError) throw new UnexpectedApiError(e);
        return e;
      },
    );
  }
}
